import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fetchAdmins, addAdmin, removeAdmin, fetchDbConfig, testDbConfig, saveDbConfig, resetDbConfig, getFilesBaseUrl, setFilesBaseUrl, getDriveConfig, testDriveConfig, saveDriveConfig, fetchMongoMigrationSchema, previewMongoMigrationImport, runMongoMigrationImport, fetchMongoMigrationImportProgress, checkDbStatus, fetchAccessMe, fetchAccessPermissions, fetchAccessRoles, fetchAccessUsers, upsertAccessUser, deleteAccessUser, searchAccessPeople, upsertAccessRole, deleteAccessRole, advisorCheckPermission, advisorCheckRole, advisorAnalyseAssignment, advisorChat, advisorApply, advisorUndo, advisorHistory, fetchRecentQuoteChanges, fetchEmailRouting, saveEmailRouting, sendTestEmail, fetchPwaBranding, updatePwaBranding, resetPwaBranding, uploadPwaIcon } from "@/lib/api";
import { refreshBranding } from "@/lib/branding";
import { TOURS } from "@/lib/tours";
import AllToursModal from "@/components/AllToursModal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { X, Plus, ShieldCheck, Trash, Warning, Database, ArrowCounterClockwise, CheckCircle, Key, MagnifyingGlass, UserCircle, Robot, PaperPlaneRight, Sparkle, ArrowUUpLeft, Clock, Receipt, EnvelopeSimple, DeviceMobile, Upload, ArrowsClockwise, BookOpen, ArrowUp, ArrowDown } from "@phosphor-icons/react";

// Hard-coded defaults used to pre-fill the DB form the first time an admin opens it.
// Matches the creds currently in backend/.env so new deploys can recover from a bad env push.
const DB_DEFAULTS = {
  host: "93.127.200.65",
  database: "ipqhznmy_UsAir",
  user: "ipqhznmy_dbroot",
  password: "12_dP%0bV-;S",
};

export default function AdminPanel({ user, onClose }) {
  const [tab, setTab] = useState("admins");
  const [admins, setAdmins] = useState([]);
  const [protectedEmail, setProtectedEmail] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [dbStatus, setDbStatus] = useState(null); // { connected, primary, fallback, fallback_used }
  const [toursOpen, setToursOpen] = useState(false);

  const load = () => {
    fetchAdmins().then((d) => {
      setAdmins(d.admins);
      setProtectedEmail(d.protected);
    });
    // Fetch the probe result so we can show an in-app banner when the admin override failed.
    checkDbStatus().then(setDbStatus).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!newEmail.trim()) return;
    setError("");
    setLoading(true);
    try {
      const res = await addAdmin(newEmail, user.email);
      setAdmins(res.admins);
      setNewEmail("");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add admin");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (email) => {
    setError("");
    try {
      const res = await removeAdmin(email, user.email);
      setAdmins(res.admins);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to remove admin");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="admin-panel-overlay">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white rounded-sm shadow-xl border border-slate-200 overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck size={20} weight="bold" className="text-blue-700" />
            <h2 className="text-lg font-bold font-['Chivo']">Admin Settings</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
            <X size={18} />
          </button>
        </div>

        {/* DB fallback banner — admin override is down; app is running on env default */}
        {dbStatus?.fallback_used && (
          <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 flex items-start gap-2.5" data-testid="admin-db-fallback-banner">
            <Warning size={16} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0 text-xs leading-snug">
              <div className="font-bold text-amber-900">Currently using ENV default database</div>
              <div className="text-amber-800 mt-0.5">
                Admin-override host <span className="font-mono">{dbStatus?.primary?.host_prefix}</span> was unreachable.
                Running on default <span className="font-mono">{dbStatus?.fallback?.host_prefix}</span>.
                Fix the override in the <button onClick={() => setTab("database")} className="underline font-semibold">Database</button> tab.
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-slate-200 bg-slate-50">
          <button data-testid="admin-tab-admins" onClick={() => setTab("admins")}
            className={`flex-1 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${
              tab === "admins" ? "border-blue-700 text-blue-700 bg-white" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            Admins
          </button>
          <button data-testid="admin-tab-database" onClick={() => setTab("database")}
            className={`flex-1 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors flex items-center justify-center gap-1.5 ${
              tab === "database" ? "border-blue-700 text-blue-700 bg-white" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            <Database size={13} weight="bold" /> Database
          </button>
          <button data-testid="admin-tab-rights" onClick={() => setTab("rights")}
            className={`flex-1 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors flex items-center justify-center gap-1.5 ${
              tab === "rights" ? "border-blue-700 text-blue-700 bg-white" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            <Key size={13} weight="bold" /> Rights
          </button>
          <button data-testid="admin-tab-activity" onClick={() => setTab("activity")}
            className={`flex-1 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors flex items-center justify-center gap-1.5 ${
              tab === "activity" ? "border-blue-700 text-blue-700 bg-white" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            <Clock size={13} weight="bold" /> Activity
          </button>
          <button data-testid="admin-tab-email" onClick={() => setTab("email")}
            className={`flex-1 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors flex items-center justify-center gap-1.5 ${
              tab === "email" ? "border-blue-700 text-blue-700 bg-white" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            <EnvelopeSimple size={13} weight="bold" /> Email
          </button>
          <button data-testid="admin-tab-pwa" onClick={() => setTab("pwa")}
            className={`flex-1 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors flex items-center justify-center gap-1.5 ${
              tab === "pwa" ? "border-blue-700 text-blue-700 bg-white" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            <DeviceMobile size={13} weight="bold" /> PWA
          </button>
          <button data-testid="admin-tab-kb" onClick={() => setTab("kb")}
            className={`flex-1 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors flex items-center justify-center gap-1.5 ${
              tab === "kb" ? "border-blue-700 text-blue-700 bg-white" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            <BookOpen size={13} weight="bold" /> KB URLs
          </button>
          <button data-testid="admin-tab-evidence" onClick={() => setTab("evidence")}
            className={`flex-1 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors flex items-center justify-center gap-1.5 ${
              tab === "evidence" ? "border-blue-700 text-blue-700 bg-white" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            <Sparkle size={13} weight="bold" /> Evidence
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
        {tab === "admins" && (
          <>
            <p className="text-xs text-slate-500">
              Only emails ending with <b>@aircompressor.com</b>, <b>@usaircompressor.com</b>, or <b>@aircompressorcanada.com</b> can be admins.
            </p>

            {error && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-sm border border-red-200">
                <Warning size={14} weight="bold" /> {error}
              </div>
            )}

            {/* Add new admin */}
            <div className="flex gap-2">
              <Input
                data-testid="admin-add-email-input"
                placeholder="email@usaircompressor.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                className="flex-1 rounded-sm border-slate-300 h-9 text-sm"
              />
              <Button
                data-testid="admin-add-button"
                disabled={loading || !newEmail.trim()}
                onClick={handleAdd}
                className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 px-3 text-sm font-semibold"
              >
                <Plus size={14} weight="bold" className="mr-1" /> Add
              </Button>
            </div>

            {/* Admin list */}
            <div className="space-y-1">
              {admins.map((email) => (
                <div key={email}
                  data-testid={`admin-row-${email}`}
                  className="flex items-center justify-between px-3 py-2 rounded-sm border border-slate-100 hover:bg-slate-50">
                  <span className="text-sm text-slate-800">{email}</span>
                  {email === protectedEmail ? (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-sm">
                      Protected
                    </span>
                  ) : (
                    <button
                      data-testid={`admin-remove-${email}`}
                      onClick={() => handleRemove(email)}
                      className="w-7 h-7 flex items-center justify-center rounded-sm border border-red-200 hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
                    >
                      <Trash size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "database" && <DbCredentialsForm user={user} />}
        {tab === "rights" && <RightsPanel user={user} />}
        {tab === "activity" && <ActivityPanel user={user} />}
        {tab === "email" && <EmailPanel user={user} />}
        {tab === "pwa" && <PwaBrandingPanel user={user} />}

        {tab === "kb" && <KnowledgeBaseUrlsPanel user={user} />}

        {tab === "evidence" && <EvidenceLibraryPanel user={user} />}
        </div>

        {/* iter146 — Persistent admin footer with a quick "View all tours"
            link. Lives outside the scrollable tab body so super-admins can
            always reach it regardless of which tab they're looking at. */}
        <div className="px-6 py-2 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-3 text-[11px] text-slate-500">
          <span>Admin Settings · {TOURS.length} interactive tours available</span>
          <button
            data-testid="admin-footer-view-tours"
            onClick={() => setToursOpen(true)}
            className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 hover:underline font-semibold"
          >
            <BookOpen size={11} weight="bold" /> View all tours
          </button>
        </div>
      </div>

      <AllToursModal open={toursOpen} onClose={() => setToursOpen(false)} user={user} />
    </div>
  );
}

function DbCredentialsForm({ user }) {
  const [form, setForm] = useState(DB_DEFAULTS);
  const [source, setSource] = useState("env");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    fetchDbConfig(user.email)
      .then((d) => {
        setForm((f) => ({
          host: d.host || f.host,
          database: d.database || f.database,
          user: d.user || f.user,
          // Don't pre-fill the actual password — show defaults only when source is env and unchanged.
          password: d.source === "mongo_override" ? "" : f.password,
        }));
        setSource(d.source || "env");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user.email]);

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setTestResult(null); };

  const resetDefaults = () => {
    setForm(DB_DEFAULTS);
    setTestResult(null);
    setMsg("");
    setErr("");
  };

  const handleTest = async () => {
    setTesting(true); setErr(""); setMsg(""); setTestResult(null);
    try {
      const r = await testDbConfig({ ...form, requester_email: user.email });
      setTestResult(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  const handleSave = async () => {
    setSaving(true); setErr(""); setMsg(""); setTestResult(null);
    try {
      const r = await saveDbConfig({ ...form, requester_email: user.email });
      setSource(r.source);
      setMsg("Saved. New connections now use these credentials.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const handleRevert = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Revert to environment defaults and delete the stored override?")) return;
    setSaving(true); setErr(""); setMsg("");
    try {
      const r = await resetDbConfig(user.email);
      setSource(r.source);
      setMsg("Reverted to environment defaults.");
      // Reload to pick up env values
      const d = await fetchDbConfig(user.email);
      setForm({ host: d.host, database: d.database, user: d.user, password: "" });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Revert failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-slate-500">Loading current credentials…</div>;

  return (
    <div className="space-y-4" data-testid="db-config-form">
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-slate-500">Active source:</span>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${
          source === "mongo_override"
            ? "text-blue-700 bg-blue-50 border-blue-200"
            : "text-slate-600 bg-slate-50 border-slate-200"
        }`} data-testid="db-config-source">
          {source === "mongo_override" ? "Saved Override" : "Environment (.env)"}
        </span>
      </div>
      <p className="text-xs text-slate-500">
        Credentials are stored in MongoDB so they survive redeploys. If the deployed app shows "Cannot load data", update and save here — no redeploy required.
      </p>

      {err && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-sm border border-red-200">
          <Warning size={14} weight="bold" /> {err}
        </div>
      )}
      {msg && (
        <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-sm border border-green-200">
          <CheckCircle size={14} weight="bold" /> {msg}
        </div>
      )}
      {testResult && (
        <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-sm border ${
          testResult.ok
            ? "text-green-700 bg-green-50 border-green-200"
            : "text-red-700 bg-red-50 border-red-200"
        }`} data-testid="db-config-test-result">
          {testResult.ok ? <CheckCircle size={14} weight="bold" /> : <Warning size={14} weight="bold" />}
          {testResult.message}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3">
        <Field label="Host / IP" testId="db-host" value={form.host} onChange={set("host")} placeholder={DB_DEFAULTS.host} />
        <Field label="Database" testId="db-name" value={form.database} onChange={set("database")} placeholder={DB_DEFAULTS.database} />
        <Field label="User" testId="db-user" value={form.user} onChange={set("user")} placeholder={DB_DEFAULTS.user} />
        <Field label="Password" testId="db-pass" value={form.password} onChange={set("password")} type="password" placeholder={source === "mongo_override" ? "(re-enter to change)" : DB_DEFAULTS.password} />
      </div>

      <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
        <Button data-testid="db-test-button" type="button" onClick={handleTest} disabled={testing}
          className="rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 h-9 px-3 text-sm font-semibold">
          {testing ? "Testing…" : "Test Connection"}
        </Button>
        <Button data-testid="db-save-button" type="button" onClick={handleSave} disabled={saving || !form.password}
          className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 px-3 text-sm font-semibold">
          {saving ? "Saving…" : "Save Credentials"}
        </Button>
        <Button data-testid="db-defaults-button" type="button" onClick={resetDefaults}
          className="rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 h-9 px-3 text-sm font-semibold">
          <ArrowCounterClockwise size={13} weight="bold" className="mr-1" /> Pre-fill Defaults
        </Button>
        {source === "mongo_override" && (
          <Button data-testid="db-revert-button" type="button" onClick={handleRevert} disabled={saving}
            className="rounded-sm border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 h-9 px-3 text-sm font-semibold">
            Revert to .env
          </Button>
        )}
      </div>

      <MongoMigrationImportSection user={user} />
      <FilesBaseUrlSection user={user} />
      <DriveConfigSection user={user} />
    </div>
  );
}

function MongoMigrationImportSection({ user }) {
  const isSuper = Boolean(user?.is_super_admin);
  const [schema, setSchema] = useState(null);
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("replace");
  const [confirmed, setConfirmed] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState("");
  const [progress, setProgress] = useState(null);
  const [importId, setImportId] = useState("");
  const [serverProgress, setServerProgress] = useState(null);
  const [err, setErr] = useState("");

  const migrationErrorMessage = (e, fallback) => {
    const status = e?.response?.status;
    const detail = e?.response?.data?.detail;
    if (status === 502 || status === 413) {
      return "Upload was rejected by the proxy layer. Redeploy the frontend image with the larger nginx upload limit, and make sure Apache/HTTPS proxy timeout is raised for large migration packages.";
    }
    return detail || fallback;
  };

  useEffect(() => {
    if (!isSuper) return;
    fetchMongoMigrationSchema()
      .then(setSchema)
      .catch(() => {});
  }, [isSuper]);

  useEffect(() => {
    if (!importId || busy !== "import") return undefined;
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      try {
        const p = await fetchMongoMigrationImportProgress(importId);
        if (cancelled) return;
        if (p?.ok) {
          setServerProgress(p);
          if (p.status === "failed") {
            setErr(p.message || "Import failed");
            setBusy("");
            return;
          }
          if (p.status === "complete") {
            setErr("");
            setBusy("");
            toast.success("MongoDB import completed");
            return;
          }
        }
      } catch {
        // Best-effort: a transient polling miss should not hide the main import.
      }
      if (!cancelled) timer = window.setTimeout(poll, 1000);
    };
    timer = window.setTimeout(poll, 1000);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [busy, importId]);

  const onFileChange = (e) => {
    setFile(e.target.files?.[0] || null);
    setPreview(null);
    setResult(null);
    setErr("");
    setConfirmed(false);
    setProgress(null);
    setImportId("");
    setServerProgress(null);
  };

  const formatBytes = (bytes) => {
    const n = Number(bytes || 0);
    if (n >= 1024 * 1024 * 1024) return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${n} B`;
  };

  const setUploadProgress = (next) => {
    setProgress({
      loaded: next.loaded,
      total: next.total,
      percent: Math.min(100, Math.max(0, next.percent || 0)),
    });
  };

  const handlePreview = async () => {
    if (!file) return;
    setBusy("preview"); setErr(""); setResult(null); setPreview(null); setProgress({ loaded: 0, total: file.size, percent: 0 });
    try {
      setPreview(await previewMongoMigrationImport(file, { onProgress: setUploadProgress }));
    } catch (e) {
      setErr(migrationErrorMessage(e, "Preview failed"));
    } finally {
      setBusy("");
    }
  };

  const handleImport = async () => {
    if (!file || !confirmed) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Import ${file.name} into MongoDB using ${mode.toUpperCase()} mode?`)) return;
    const nextImportId = `mongo_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    setImportId(nextImportId);
    setServerProgress({
      status: "uploading",
      phase: "uploading",
      message: "Uploading package",
      total_docs: totalDocs,
      processed_docs: 0,
      written_docs: 0,
    });
    setBusy("import"); setErr(""); setResult(null); setProgress({ loaded: 0, total: file.size, percent: 0 });
    let keepPolling = false;
    try {
      const r = await runMongoMigrationImport(file, {
        mode,
        confirm: true,
        importId: nextImportId,
        onProgress: setUploadProgress,
      });
      setResult(r);
      setConfirmed(false);
      setServerProgress((p) => ({
        ...(p || {}),
        status: "complete",
        phase: "complete",
        message: "Import complete",
        written_docs: r.collections?.reduce((sum, c) => sum + Number(c.written || 0), 0) || totalDocs,
        total_docs: totalDocs,
      }));
      toast.success("MongoDB import completed");
    } catch (e) {
      try {
        const p = await fetchMongoMigrationImportProgress(nextImportId);
        if (p?.ok && p.status && p.status !== "failed") {
          setServerProgress(p);
          setErr("The import request disconnected, but the server is still reporting progress. Keep this panel open to monitor it.");
          keepPolling = true;
          return;
        }
      } catch {
        // Fall through to the original request error.
      }
      setErr(migrationErrorMessage(e, "Import failed"));
    } finally {
      if (!keepPolling) setBusy("");
    }
  };

  if (!isSuper) return null;

  const collections = preview?.collections || result?.collections || [];
  const collectionDocCount = (c) => Number(c.written ?? c.validated_count ?? c.declared_count ?? c.count ?? 0);
  const totalDocs = collections.reduce((sum, c) => sum + collectionDocCount(c), 0);
  const serverTotalDocs = Number(serverProgress?.total_docs || totalDocs || 0);
  const serverDoneDocs = Number(
    serverProgress?.phase === "writing"
      ? (serverProgress?.written_docs || 0)
      : (serverProgress?.processed_docs || serverProgress?.written_docs || 0)
  );
  const serverPercent = serverProgress?.status === "complete"
    ? 100
    : serverTotalDocs > 0
      ? Math.min(100, Math.max(0, Math.round((serverDoneDocs / serverTotalDocs) * 100)))
      : 0;

  return (
    <section className="pt-4 border-t border-slate-200 space-y-3" data-testid="mongo-migration-import">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Database size={15} weight="bold" className="text-blue-700" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">MongoDB Import</h3>
            <span className="text-[10px] font-bold uppercase tracking-wider text-purple-700 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded-sm">
              Super admin
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Upload the migration zip exported from the source app. Preview checks the manifest and collection counts before any write.
          </p>
        </div>
      </div>

      {schema && (
        <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
          Expected schema: <code className="font-mono text-slate-700">{schema.schema}</code>
          <span className="mx-1">·</span>
          Enable writes with <code className="font-mono text-slate-700">{schema.import_enable_env}=1</code>
        </div>
      )}

      {err && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-sm border border-red-200">
          <Warning size={14} weight="bold" /> {err}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3">
        <label className="text-sm">
          <span className="block text-xs font-semibold text-slate-600 mb-1">Migration zip</span>
          <input
            data-testid="mongo-import-file"
            type="file"
            accept=".zip,application/zip,application/x-zip-compressed"
            onChange={onFileChange}
            className="block w-full text-xs text-slate-600 file:mr-3 file:h-9 file:px-3 file:rounded-sm file:border file:border-slate-300 file:bg-white file:text-slate-700 file:font-semibold hover:file:bg-slate-50"
          />
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-sm">
            <span className="block text-xs font-semibold text-slate-600 mb-1">Import mode</span>
            <select
              data-testid="mongo-import-mode"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="w-full h-9 rounded-sm border border-slate-300 bg-white px-3 text-sm text-slate-700"
            >
              <option value="replace">Replace collections</option>
              <option value="merge">Merge by _id</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-600 border border-slate-200 rounded-sm px-3 py-2 mt-0 sm:mt-5">
            <input
              data-testid="mongo-import-confirm"
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="rounded border-slate-300"
            />
            I understand this writes to the current MongoDB database.
          </label>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          data-testid="mongo-import-preview-button"
          type="button"
          onClick={handlePreview}
          disabled={!file || busy === "preview"}
          className="rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 h-9 px-3 text-sm font-semibold"
        >
          <Upload size={13} weight="bold" className="mr-1" />
          {busy === "preview" ? "Previewing..." : "Preview Package"}
        </Button>
        <Button
          data-testid="mongo-import-run-button"
          type="button"
          onClick={handleImport}
          disabled={!file || !preview || !confirmed || busy === "import"}
          className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 px-3 text-sm font-semibold disabled:opacity-50"
        >
          <ArrowsClockwise size={13} weight="bold" className="mr-1" />
          {busy === "import" ? "Importing..." : "Import MongoDB"}
        </Button>
      </div>

      {progress && (
        <div className="border border-slate-200 bg-slate-50 rounded-sm px-3 py-2" data-testid="mongo-import-progress">
          <div className="flex items-center justify-between gap-3 text-[11px] text-slate-600 mb-1.5">
            <span className="font-semibold">
              {busy ? `${busy === "preview" ? "Preview" : "Import"} upload` : "Last upload"}
            </span>
            <span className="font-mono">
              {progress.percent}% · {formatBytes(progress.loaded)} / {formatBytes(progress.total)}
            </span>
          </div>
          <div className="h-2 bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div
              className="h-full bg-blue-700 transition-all duration-200"
              style={{ width: `${progress.percent}%` }}
            />
          </div>
          {busy && progress.percent >= 100 && (
            <div className="text-[11px] text-slate-500 mt-1.5">
              Upload complete. Processing package on server...
            </div>
          )}
        </div>
      )}

      {serverProgress && (
        <div className="border border-blue-200 bg-blue-50 rounded-sm px-3 py-2" data-testid="mongo-import-server-progress">
          <div className="flex items-center justify-between gap-3 text-[11px] text-blue-900 mb-1.5">
            <span className="font-semibold">
              Server import · {serverProgress.phase || serverProgress.status || "working"}
              {serverProgress.current_collection ? (
                <span className="font-mono text-blue-700"> · {serverProgress.current_collection}</span>
              ) : null}
            </span>
            <span className="font-mono">
              {serverPercent}% · {serverDoneDocs.toLocaleString()} / {serverTotalDocs.toLocaleString()} docs
            </span>
          </div>
          <div className="h-2 bg-white border border-blue-200 rounded-sm overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${
                serverProgress.status === "failed" ? "bg-red-600" :
                serverProgress.status === "complete" ? "bg-green-600" : "bg-blue-700"
              }`}
              style={{ width: `${serverPercent}%` }}
            />
          </div>
          <div className="text-[11px] text-blue-800 mt-1.5">
            {serverProgress.message || "Import is running on the server."}
            {serverProgress.updated_at ? (
              <span className="text-blue-600"> · updated {new Date(serverProgress.updated_at).toLocaleTimeString()}</span>
            ) : null}
          </div>
        </div>
      )}

      {(preview || result) && (
        <div className="border border-slate-200 rounded-sm overflow-hidden" data-testid="mongo-import-summary">
          <div className="bg-slate-50 px-3 py-2 text-xs text-slate-600 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span><b>{collections.length}</b> collections</span>
            <span><b>{totalDocs}</b> docs</span>
            {(preview?.source_db || result?.source_db) && <span>source <code>{preview?.source_db || result?.source_db}</code></span>}
            {result && <span className="text-green-700 font-semibold">completed</span>}
          </div>
          <div className="max-h-40 overflow-y-auto divide-y divide-slate-100">
            {collections.slice(0, 30).map((c) => (
              <div key={c.name} className="px-3 py-1.5 text-xs flex items-center justify-between gap-3">
                <span className="font-mono text-slate-700 truncate">{c.name}</span>
                <span className="text-slate-500 shrink-0">{collectionDocCount(c)}</span>
              </div>
            ))}
            {collections.length > 30 && (
              <div className="px-3 py-1.5 text-xs text-slate-500">+ {collections.length - 30} more collections</div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function DriveConfigSection({ user }) {
  const [form, setForm] = useState({ api_key: "", parts_folder_id: "", pictures_folder_id: "" });
  const [hasKey, setHasKey] = useState(false);
  const [maskedKey, setMaskedKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    getDriveConfig(user.email)
      .then((d) => {
        setForm({
          api_key: "",
          parts_folder_id: d.parts_folder_id || "1VfNZURbAFbDRCrUhZBro0bn48BMtPesS",
          pictures_folder_id: d.pictures_folder_id || "1hsf87mQkZudBf1XaiIpavQGv3jJpmF9a",
        });
        setHasKey(Boolean(d.has_api_key));
        setMaskedKey(d.api_key_masked || "");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user.email]);

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setTestResult(null); setMsg(""); setErr(""); };

  const test = async () => {
    setTesting(true); setErr(""); setMsg(""); setTestResult(null);
    try {
      const r = await testDriveConfig({ ...form, requester_email: user.email });
      setTestResult(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  const save = async () => {
    setSaving(true); setErr(""); setMsg("");
    try {
      await saveDriveConfig({ ...form, requester_email: user.email });
      setMsg("Drive config saved. File lookups will use the new key within 10 minutes (or restart backend to force).");
      if (form.api_key) { setHasKey(true); setMaskedKey(form.api_key.slice(0, 4) + "…" + form.api_key.slice(-4)); }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return null;
  return (
    <div className="pt-4 border-t border-slate-200 space-y-2" data-testid="drive-config-section">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Google Drive</span>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${
          hasKey ? "text-blue-700 bg-blue-50 border-blue-200" : "text-amber-700 bg-amber-50 border-amber-200"
        }`}>{hasKey ? `Key set (${maskedKey})` : "No API key"}</span>
      </div>
      <p className="text-[11px] text-slate-500">
        Files are looked up by filename in the two Drive folders below. Get a free API key at{" "}
        <a className="text-blue-700 underline" href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer">console.cloud.google.com/apis/credentials</a>,
        then enable the <b>Google Drive API</b> in APIs & Services → Library. Real keys start with <code className="font-mono bg-slate-100 px-1 rounded-sm">AIza…</code> (39 chars).
      </p>

      <div className="grid grid-cols-1 gap-2">
        <div>
          <label className="text-[11px] font-semibold text-slate-600 mb-0.5 block">API Key {hasKey && <span className="text-[10px] text-slate-400 font-normal">(leave blank to keep existing)</span>}</label>
          <Input data-testid="drive-api-key" type="password" value={form.api_key} onChange={set("api_key")}
            placeholder={hasKey ? maskedKey : "AIza..."}
            className="h-9 text-sm font-mono rounded-sm border-slate-300" />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-slate-600 mb-0.5 block">Parts Folder ID (documents)</label>
          <Input data-testid="drive-parts-folder" value={form.parts_folder_id} onChange={set("parts_folder_id")}
            className="h-9 text-sm font-mono rounded-sm border-slate-300" />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-slate-600 mb-0.5 block">Pictures Folder ID</label>
          <Input data-testid="drive-pictures-folder" value={form.pictures_folder_id} onChange={set("pictures_folder_id")}
            className="h-9 text-sm font-mono rounded-sm border-slate-300" />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 pt-1">
        <Button type="button" data-testid="drive-test" onClick={test} disabled={testing || !form.api_key}
          className="rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 h-9 px-3 text-sm font-semibold">
          {testing ? "Testing…" : "Test Key + Folder"}
        </Button>
        <Button type="button" data-testid="drive-save" onClick={save} disabled={saving}
          className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 px-3 text-sm font-semibold">
          {saving ? "Saving…" : "Save Drive Config"}
        </Button>
      </div>

      {testResult && (
        <div className={`text-xs px-3 py-2 rounded-sm border ${
          testResult.ok ? "text-green-700 bg-green-50 border-green-200" : "text-red-700 bg-red-50 border-red-200"
        }`} data-testid="drive-test-result">
          <div className="font-semibold">{testResult.message}</div>
          {testResult.ok && testResult.sample?.length > 0 && (
            <ul className="mt-1 list-disc pl-4 text-slate-600 text-[11px]">
              {testResult.sample.map((n) => <li key={n} className="truncate">{n}</li>)}
            </ul>
          )}
        </div>
      )}
      {msg && <div className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded-sm border border-green-200">{msg}</div>}
      {err && <div className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded-sm border border-red-200">{err}</div>}
    </div>
  );
}

function FilesBaseUrlSection({ user }) {
  const [url, setUrlState] = useState("");
  const [source, setSource] = useState("unset");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    getFilesBaseUrl(user.email)
      .then((d) => { setUrlState(d.url || ""); setSource(d.source || "unset"); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user.email]);

  const save = async () => {
    setSaving(true); setErr(""); setMsg("");
    try {
      await setFilesBaseUrl(url.trim(), user.email);
      setSource("mongo_override");
      setMsg("Files base URL saved.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  if (loading) return null;
  return (
    <div className="pt-4 border-t border-slate-200 space-y-2" data-testid="files-base-url-section">
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Files Base URL</span>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${
          source === "mongo_override" ? "text-blue-700 bg-blue-50 border-blue-200"
          : source === "env" ? "text-slate-600 bg-slate-50 border-slate-200"
          : "text-amber-700 bg-amber-50 border-amber-200"
        }`}>{source === "mongo_override" ? "Saved" : source === "env" ? "Env" : "Unset"}</span>
      </div>
      <p className="text-[11px] text-slate-500">
        Base URL where Part_Files are hosted (e.g. <code className="font-mono bg-slate-100 px-1 rounded-sm">https://usaircompressor.com/files</code>). File paths stored in the DB are appended to this base when building download links.
      </p>
      <div className="flex gap-2">
        <Input data-testid="files-base-url-input" value={url} onChange={(e) => { setUrlState(e.target.value); setMsg(""); setErr(""); }}
          placeholder="https://example.com/files"
          className="flex-1 h-9 text-sm font-mono rounded-sm border-slate-300" />
        <Button type="button" data-testid="files-base-url-save" onClick={save} disabled={saving}
          className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 px-3 text-sm font-semibold">
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
      {msg && <div className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded-sm border border-green-200">{msg}</div>}
      {err && <div className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded-sm border border-red-200">{err}</div>}
    </div>
  );
}

function Field({ label, testId, value, onChange, type = "text", placeholder }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 mb-1 block">{label}</label>
      <Input data-testid={testId} type={type} value={value || ""} onChange={onChange} placeholder={placeholder}
        className="rounded-sm border-slate-300 h-9 text-sm font-mono" />
    </div>
  );
}


// ─── Rights (RBAC) tab ───────────────────────────────────────────
function RightsPanel({ user }) {
  const [me, setMe] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [people, setPeople] = useState([]);
  const [selected, setSelected] = useState(null);   // {email, name, source_table, source_ref, existing_rights}
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const isSuperAdmin = Boolean(me?.is_super_admin);

  const loadAll = async () => {
    try {
      const [mePayload, perms, rolesPayload, usersPayload] = await Promise.all([
        fetchAccessMe(),
        fetchAccessPermissions(),
        fetchAccessRoles(),
        fetchAccessUsers(),
      ]);
      setMe(mePayload);
      setCatalog(perms.catalog || []);
      setRoles(rolesPayload.roles || []);
      setUsers(usersPayload.users || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load");
    }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  // People search (debounced via key)
  useEffect(() => {
    let timer = setTimeout(async () => {
      try {
        const r = await searchAccessPeople({ q, limit: 15 });
        setPeople(r.results || []);
      } catch { setPeople([]); }
    }, 250);
    return () => clearTimeout(timer);
  }, [q]);

  const chooseExistingUser = (u) => {
    setSelected({
      email: u.email,
      name: u.display_name || u.email,
      source_table: u.source_table || "",
      source_ref: u.source_ref || "",
      existing_rights: u,
    });
    setMsg(""); setErr("");
  };

  const choosePerson = (p) => {
    setSelected({
      email: p.email,
      name: p.name || p.email,
      source_table: p.source_table,
      source_ref: p.source_ref || "",
      existing_rights: p.existing_rights || null,
    });
    setQ("");
    setPeople([]);
    setMsg(""); setErr("");
  };

  if (!me) {
    return <div className="text-sm text-slate-500">Loading…</div>;
  }
  if (!isSuperAdmin) {
    return (
      <div className="text-sm text-slate-600 bg-amber-50 border border-amber-200 rounded-sm p-4" data-testid="rights-not-superadmin">
        <p className="font-semibold mb-1 flex items-center gap-1.5"><Warning size={14} weight="bold" className="text-amber-600" /> Super-admin only</p>
        <p className="text-xs text-slate-600">Only <b>bora@</b> or <b>deniz@</b> at <b>usaircompressor.com</b> / <b>aircompressorcanada.com</b> can manage rights.</p>
        <p className="mt-2 text-xs text-slate-500">Your current role: <b>{me.role || "—"}</b> · Permissions: {me.permissions.length}.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="rights-panel">
      {/* Rights Agent — natural-language duplicate detector */}
      <RightsAgent roles={roles} onRoleCreated={loadAll} />

      {/* Create / extend role template */}
      <RoleTemplateEditor roles={roles} catalog={catalog} onChanged={loadAll} />

      {/* Recent agent actions */}
      <AgentHistoryPanel />

      {/* People search */}
      <div>
        <h3 className="text-xs font-black uppercase tracking-[0.15em] text-slate-500 mb-2">Find a person</h3>
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="rights-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search UsAirPeople, customer contacts, supplier contacts…"
            className="pl-9 rounded-sm border-slate-300 h-9 text-sm" />
        </div>
        {people.length > 0 && (
          <ul className="mt-2 border border-slate-200 rounded-sm bg-white divide-y divide-slate-100 max-h-56 overflow-y-auto" data-testid="rights-people-results">
            {people.map((p) => {
              const label = p.source_table === "usair_people" ? "STAFF" : p.source_table === "customer_persons" ? "CUSTOMER" : "SUPPLIER";
              const tone = p.source_table === "usair_people" ? "bg-blue-50 text-blue-700 border-blue-200" : p.source_table === "customer_persons" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200";
              return (
                <li key={`${p.source_table}-${p.email}`}>
                  <button onClick={() => choosePerson(p)} data-testid={`rights-person-${p.email}`}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-3 text-sm">
                    <span className={`text-[9px] font-bold border px-1.5 py-0.5 rounded-sm ${tone}`}>{label}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{p.name || p.email}</p>
                      <p className="text-[11px] text-slate-500 truncate">{p.email}{p.extras?.company ? ` · ${p.extras.company}` : ""}</p>
                    </div>
                    {p.existing_rights && <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-sm">ROLE: {p.existing_rights.role || "—"}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Existing users */}
      <div>
        <h3 className="text-xs font-black uppercase tracking-[0.15em] text-slate-500 mb-2">Users with explicit rights ({users.length})</h3>
        {users.length === 0 ? (
          <p className="text-xs text-slate-400 italic">No users yet. Find someone above to assign a role.</p>
        ) : (
          <ul className="border border-slate-200 rounded-sm bg-white divide-y divide-slate-100 max-h-60 overflow-y-auto" data-testid="rights-users-list">
            {users.map((u) => (
              <li key={u.email}>
                <button onClick={() => chooseExistingUser(u)} data-testid={`rights-user-${u.email}`}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-3 text-sm">
                  <UserCircle size={16} className="text-slate-400" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{u.display_name || u.email}</p>
                    <p className="text-[11px] text-slate-500 truncate">{u.email} · Updated {(u.updated_at || "").slice(0, 10)} by {u.updated_by || "—"}</p>
                  </div>
                  <span className="text-[9px] font-bold text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-sm">{u.role || "—"}</span>
                  <span className="text-[10px] font-mono text-slate-500">{(u.effective_permissions || []).length} perms</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Editor */}
      {selected && (
        <RightsEditor
          key={`editor-${selected.email}`}
          subject={selected}
          roles={roles}
          catalog={catalog}
          saving={saving}
          onSave={async ({ role, granted, revoked }) => {
            setSaving(true); setErr(""); setMsg("");
            try {
              await upsertAccessUser({
                email: selected.email, role, granted, revoked,
                source_table: selected.source_table,
                source_ref: selected.source_ref,
                display_name: selected.name,
              });
              setMsg(`Saved rights for ${selected.email}`);
              await loadAll();
              setSelected((s) => s ? ({ ...s, existing_rights: { ...s.existing_rights, role, granted, revoked } }) : s);
            } catch (e) {
              setErr(e?.response?.data?.detail || "Save failed");
            } finally { setSaving(false); }
          }}
          onDelete={async () => {
            // eslint-disable-next-line no-alert
            if (!window.confirm(`Remove all explicit rights for ${selected.email}? They'll fall back to whatever the system grants by default.`)) return;
            setSaving(true); setErr(""); setMsg("");
            try {
              await deleteAccessUser(selected.email);
              setMsg(`Removed rights for ${selected.email}`);
              setSelected(null);
              await loadAll();
            } catch (e) {
              setErr(e?.response?.data?.detail || "Delete failed");
            } finally { setSaving(false); }
          }}
          onCancel={() => setSelected(null)}
        />
      )}

      {msg && <div className="text-xs text-green-700 bg-green-50 px-3 py-2 rounded-sm border border-green-200" data-testid="rights-msg">{msg}</div>}
      {err && <div className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-sm border border-red-200" data-testid="rights-err">{err}</div>}
    </div>
  );
}

function RightsEditor({ subject, roles, catalog, saving, onSave, onDelete, onCancel }) {  const initial = subject.existing_rights || {};
  const [role, setRole] = useState(initial.role || "");
  const [granted, setGranted] = useState(new Set(initial.granted || []));
  const [revoked, setRevoked] = useState(new Set(initial.revoked || []));

  const activeRole = roles.find((r) => r.role === role);
  const basePerms = new Set(activeRole?.permissions || []);

  const effectiveSet = new Set([...basePerms, ...granted]);
  for (const r of revoked) effectiveSet.delete(r);

  const togglePerm = (key) => {
    // Cycle: base → granted extra → revoked → base (if in base)
    // or base → granted → off (if not in base)
    const inBase = basePerms.has(key);
    const isGranted = granted.has(key);
    const isRevoked = revoked.has(key);
    const nextG = new Set(granted); const nextR = new Set(revoked);
    if (inBase) {
      if (!isRevoked) { nextR.add(key); nextG.delete(key); }
      else { nextR.delete(key); }
    } else {
      if (!isGranted) { nextG.add(key); nextR.delete(key); }
      else { nextG.delete(key); }
    }
    setGranted(nextG); setRevoked(nextR);
  };

  return (
    <div className="border border-slate-200 rounded-sm p-4 bg-slate-50" data-testid="rights-editor">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold text-slate-800">{subject.name}</h3>
          <p className="text-[11px] text-slate-500 font-mono">{subject.email} · source: {subject.source_table || "—"}</p>
        </div>
        <button onClick={onCancel} data-testid="rights-editor-close" className="text-slate-400 hover:text-slate-700 text-xs">Close</button>
      </div>

      <div className="mb-3">
        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Role</label>
        <select data-testid="rights-editor-role" value={role} onChange={(e) => setRole(e.target.value)}
          className="w-full rounded-sm border-slate-300 h-9 text-sm px-2">
          <option value="">— No role —</option>
          {roles.map((r) => <option key={r.role} value={r.role}>{r.label || r.role} ({r.permissions.length} perms)</option>)}
        </select>
      </div>

      {/* iter145 — One-click Make Test Admin toggle. Granting flips the
          three test_admin.* permissions on; revoking removes them.
          Operates purely on the local granted/revoked sets so the
          existing Save action persists the change atomically with any
          other matrix tweaks the super-admin made in this session. */}
      {(() => {
        const TEST_ADMIN_TRIPLE = ["test_admin.access", "test_admin.approve", "test_admin.manage_queue"];
        const allOn = TEST_ADMIN_TRIPLE.every((p) => granted.has(p) || basePerms.has(p));
        const toggleTestAdmin = () => {
          const nextG = new Set(granted);
          const nextR = new Set(revoked);
          if (allOn) {
            TEST_ADMIN_TRIPLE.forEach((p) => { nextG.delete(p); nextR.delete(p); });
          } else {
            TEST_ADMIN_TRIPLE.forEach((p) => { nextG.add(p); nextR.delete(p); });
          }
          setGranted(nextG); setRevoked(nextR);
        };
        return (
          <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-sm bg-emerald-50 border border-emerald-200">
            <button type="button" onClick={toggleTestAdmin}
                    data-testid="rights-editor-make-test-admin"
                    className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm ${allOn ? "bg-rose-600 text-white" : "bg-emerald-600 text-white"} hover:opacity-90`}>
              {allOn ? "Remove Test Admin" : "Make Test Admin"}
            </button>
            <span className="text-[11px] text-emerald-900">
              {allOn
                ? "User has the Test Admin triple. Save to persist."
                : "Grant the test_admin.access / approve / manage_queue triple in one click."}
            </span>
          </div>
        );
      })()}

      {/* iter147 — One-click Make HR Admin toggle. Mirrors the test-admin
          shortcut above but flips the hr_admin.* triple. */}
      {(() => {
        const HR_ADMIN_TRIPLE = ["hr_admin.access", "hr_admin.manage_library", "hr_admin.publish"];
        const allOn = HR_ADMIN_TRIPLE.every((p) => granted.has(p) || basePerms.has(p));
        const toggleHrAdmin = () => {
          const nextG = new Set(granted);
          const nextR = new Set(revoked);
          if (allOn) {
            HR_ADMIN_TRIPLE.forEach((p) => { nextG.delete(p); nextR.delete(p); });
          } else {
            HR_ADMIN_TRIPLE.forEach((p) => { nextG.add(p); nextR.delete(p); });
          }
          setGranted(nextG); setRevoked(nextR);
        };
        return (
          <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-sm bg-indigo-50 border border-indigo-200">
            <button type="button" onClick={toggleHrAdmin}
                    data-testid="rights-editor-make-hr-admin"
                    className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm ${allOn ? "bg-rose-600 text-white" : "bg-indigo-600 text-white"} hover:opacity-90`}>
              {allOn ? "Remove HR Admin" : "Make HR Admin"}
            </button>
            <span className="text-[11px] text-indigo-900">
              {allOn
                ? "User has the HR Admin triple. Save to persist."
                : "Grant hr_admin.access / manage_library / publish in one click."}
            </span>
          </div>
        );
      })()}

      <div className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="rights-editor-matrix">
        {catalog.map((cat) => (
          <div key={cat.category}>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-2">{cat.category}</p>
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-sm bg-white">
              {cat.items.map((perm) => {
                const inBase = basePerms.has(perm.key);
                const isGranted = granted.has(perm.key);
                const isRevoked = revoked.has(perm.key);
                const isEffective = effectiveSet.has(perm.key);
                const state = inBase ? (isRevoked ? "revoked" : "base")
                  : (isGranted ? "granted" : "off");
                const stateClass = {
                  base: "bg-blue-50 border-blue-200 text-blue-700",
                  granted: "bg-emerald-50 border-emerald-200 text-emerald-700",
                  revoked: "bg-red-50 border-red-200 text-red-700",
                  off: "bg-slate-100 border-slate-200 text-slate-400",
                }[state];
                return (
                  <li key={perm.key} className="flex items-center gap-2 px-3 py-2 text-sm">
                    <button onClick={() => togglePerm(perm.key)} data-testid={`rights-perm-${perm.key}`}
                      className={`text-[9px] font-bold border px-2 py-1 rounded-sm w-24 text-center ${stateClass}`}>
                      {state === "base" ? "FROM ROLE" : state === "granted" ? "+ GRANTED" : state === "revoked" ? "✕ REVOKED" : "— OFF"}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold truncate ${isEffective ? "text-slate-800" : "text-slate-400"}`}>{perm.label}</p>
                      <p className="text-[10px] text-slate-400 font-mono truncate">{perm.key}</p>
                    </div>
                    {isEffective && <CheckCircle size={14} weight="fill" className="text-emerald-500" />}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 mt-4">
        <Button data-testid="rights-editor-save" disabled={saving}
          onClick={() => onSave({ role, granted: [...granted], revoked: [...revoked] })}
          className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 px-3 text-sm font-semibold">
          {saving ? "Saving…" : "Save rights"}
        </Button>
        {subject.existing_rights && (
          <Button data-testid="rights-editor-delete" disabled={saving} onClick={onDelete}
            className="rounded-sm bg-white border border-red-200 text-red-600 hover:bg-red-50 h-9 px-3 text-sm font-semibold">
            Remove explicit rights
          </Button>
        )}
        <div className="ml-auto text-[11px] text-slate-500">Effective permissions: <b>{effectiveSet.size}</b></div>
      </div>
    </div>
  );
}


// ─── Rights Agent (natural-language overlap detector) ────────────
function RightsAgent({ roles, onRoleCreated }) {
  const [prompt, setPrompt] = useState("");
  const [thinking, setThinking] = useState(false);
  const [response, setResponse] = useState(null);
  const [err, setErr] = useState("");
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(null);
  const [undoSecondsLeft, setUndoSecondsLeft] = useState(0);
  const [undoing, setUndoing] = useState(false);
  const [undone, setUndone] = useState(false);

  // Countdown timer — starts when `applied` arrives with an apply_id
  useEffect(() => {
    if (!applied?.apply_id || undone) { setUndoSecondsLeft(0); return; }
    const deadline = applied.undo_deadline_at
      ? Date.parse(applied.undo_deadline_at)
      : Date.now() + (applied.undo_window_seconds || 60) * 1000;
    const tick = () => {
      const remaining = Math.max(0, Math.round((deadline - Date.now()) / 1000));
      setUndoSecondsLeft(remaining);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [applied, undone]);

  const doUndo = async () => {
    if (!applied?.apply_id) return;
    setUndoing(true); setErr("");
    try {
      await advisorUndo(applied.apply_id);
      setUndone(true);
      onRoleCreated?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : (d?.message || e?.message || "Undo failed"));
    } finally { setUndoing(false); }
  };

  const run = async () => {
    if (!prompt.trim()) return;
    setThinking(true); setErr(""); setResponse(null); setApplied(null); setUndone(false);
    try {
      const r = await advisorChat(prompt.trim());
      setResponse(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Agent unavailable");
    } finally { setThinking(false); }
  };

  // ── "Apply" availability rules ────────────────────────────────
  //   create_permission  → need permission_key (any label); blocks on hard-conflict
  //   create_role        → need role_key and ≥1 permission; blocks on hard-conflict
  //   assign_permission  → need role_key + permission_key; always safe (noop on dup)
  const intent = response?.intent?.intent || "";
  const hardPermConflict = (response?.permission_conflicts || []).some((h) => (h.similarity || 0) >= 0.9);
  const hardRoleConflict = (response?.role_conflicts || []).some((h) => (h.similarity || 0) >= 0.9);
  const canApply = (() => {
    if (!response || applied) return false;
    if (intent === "create_permission") return Boolean(response.intent?.permission_key) && !hardPermConflict;
    if (intent === "create_role") return Boolean(response.intent?.role_key) && (response.intent?.permissions?.length || 0) > 0 && !hardRoleConflict;
    if (intent === "assign_permission") return Boolean(response.intent?.role_key && response.intent?.permission_key);
    return false;
  })();

  const apply = async (force = false) => {
    if (!response?.intent) return;
    const payload = {
      intent,
      permission_key: response.intent.permission_key || "",
      permission_label: response.intent.permission_label || "",
      permission_category: response.intent.permission_category || "Other",
      role_key: response.intent.role_key || "",
      role_label: response.intent.role_label || "",
      role_description: response.intent.rationale || "",
      permissions: response.intent.permissions || [],
      force,
    };
    setApplying(true); setErr(""); setUndone(false);
    try {
      const r = await advisorApply(payload);
      setApplied(r);
      onRoleCreated?.();
    } catch (e) {
      setErr(typeof e?.response?.data?.detail === "string"
        ? e.response.data.detail
        : (e?.response?.data?.detail?.message || e?.message || "Apply failed"));
    } finally { setApplying(false); }
  };

  const examples = [
    "Create a right to let staff look at customer data",
    "Add a role called Warehouse Clerk with view-only access to parts",
    "Grant can_create_quote to the sales_manager role",
  ];

  return (
    <div data-testid="rights-agent" className="border border-indigo-100 bg-indigo-50/60 rounded-sm p-4">
      <div className="flex items-center gap-2 mb-2">
        <Robot size={16} weight="bold" className="text-indigo-600" />
        <h3 className="text-sm font-bold text-indigo-900 font-['Chivo']">Access-control agent</h3>
        <span className="text-[9px] font-bold bg-indigo-600 text-white px-1.5 py-0.5 rounded-sm">AI</span>
      </div>
      <p className="text-[11px] text-slate-600 mb-3">
        Describe a permission or position in plain English. The agent checks for overlaps before creating anything new.
      </p>
      <div className="flex gap-2">
        <Input data-testid="rights-agent-prompt" value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !thinking) run(); }}
          placeholder="e.g. 'Create a Warehouse Clerk role'"
          className="rounded-sm border-indigo-200 h-9 text-sm" />
        <Button data-testid="rights-agent-send" onClick={run} disabled={thinking || !prompt.trim()}
          className="rounded-sm bg-indigo-600 text-white hover:bg-indigo-700 h-9 px-3 text-sm font-semibold">
          {thinking ? "Thinking…" : (<><PaperPlaneRight size={12} weight="bold" className="inline mr-1" />Ask</>)}
        </Button>
      </div>

      {!response && !thinking && (
        <div className="mt-2 flex flex-wrap gap-1">
          {examples.map((e) => (
            <button key={e} data-testid={`rights-agent-ex-${e.slice(0, 16).replace(/\s/g, '-')}`}
              onClick={() => setPrompt(e)}
              className="text-[10px] px-2 py-1 rounded-sm border border-indigo-200 bg-white text-indigo-700 hover:bg-indigo-100">
              {e}
            </button>
          ))}
        </div>
      )}

      {err && <div data-testid="rights-agent-err" className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2">{err}</div>}

      {response && (
        <div className="mt-3 space-y-3" data-testid="rights-agent-response">
          <div className="bg-white border border-indigo-200 rounded-sm p-3">
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-indigo-600 mb-1">
              <Sparkle size={10} weight="fill" />Agent verdict
            </div>
            <p className="text-sm text-slate-800">{(response.suggestion || "—").slice(0, 2000)}</p>
            <div className="mt-1.5 flex flex-wrap gap-1 text-[10px] font-mono text-slate-500">
              <span>intent: <b>{response.intent?.intent || "unknown"}</b></span>
              {response.intent?.role_key && <span>· role: <b className="text-blue-700">{response.intent.role_key}</b></span>}
              {response.intent?.permission_key && <span>· permission: <b className="text-emerald-700">{response.intent.permission_key}</b></span>}
              {Array.isArray(response.intent?.permissions) && response.intent.permissions.length > 0 && (
                <span>· +{response.intent.permissions.length} perms</span>
              )}
            </div>
            {applied ? (
              <div data-testid="rights-agent-applied" className="mt-3 space-y-2">
                <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2 text-emerald-800 flex items-center gap-2">
                  <CheckCircle size={14} weight="fill" className="text-emerald-600" />
                  <div className="flex-1">
                    <b>{undone ? "Undone" : "Applied"}</b> — {undone ? (
                      applied.applied === "create_permission" ? `Permission '${applied.permission?.key}' removed from catalog.`
                      : applied.applied === "create_role" ? `Role '${applied.role?.role}' reverted.`
                      : applied.applied === "assign_permission" ? `Reverted additions on '${applied.role?.role}'.`
                      : "Reverted."
                    ) : (
                      applied.applied === "create_permission"
                        ? `Permission '${applied.permission?.key}' added to catalog.`
                      : applied.applied === "create_role"
                        ? `Role '${applied.role?.role}' saved with ${applied.role?.permissions?.length || 0} permission(s).`
                      : applied.applied === "assign_permission"
                        ? `Added to '${applied.role?.role}': ${(applied.added_permissions || []).join(", ")}`
                      : applied.applied === "noop"
                        ? (applied.message || "No changes.")
                      : "Done."
                    )}
                  </div>
                  {!undone && applied.apply_id && undoSecondsLeft > 0 && (
                    <Button data-testid="rights-agent-undo" onClick={doUndo} disabled={undoing}
                      className="rounded-sm bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-100 h-7 px-2 text-[11px] font-bold flex items-center gap-1">
                      <ArrowUUpLeft size={11} weight="bold" />
                      {undoing ? "Undoing…" : `Undo · ${undoSecondsLeft}s`}
                    </Button>
                  )}
                </div>
                {!undone && applied.apply_id && undoSecondsLeft > 0 && (
                  <div className="relative h-[3px] w-full bg-emerald-100 rounded-sm overflow-hidden" aria-hidden="true">
                    <div data-testid="rights-agent-undo-bar" className="absolute inset-y-0 left-0 bg-emerald-500 transition-[width] duration-1000 linear"
                      style={{ width: `${(undoSecondsLeft / (applied.undo_window_seconds || 60)) * 100}%` }} />
                  </div>
                )}
                {!undone && applied.apply_id && undoSecondsLeft === 0 && (
                  <p className="text-[10px] text-slate-400 italic">Undo window expired — this change is now permanent.</p>
                )}
              </div>
            ) : canApply ? (
              <div className="mt-3 flex items-center gap-2">
                <Button data-testid="rights-agent-apply" onClick={() => apply(false)} disabled={applying}
                  className="rounded-sm bg-emerald-600 text-white hover:bg-emerald-700 h-8 px-3 text-xs font-semibold">
                  {applying ? "Applying…" : "Apply this action"}
                </Button>
                <span className="text-[10px] text-slate-500">One-click commits the parsed intent.</span>
              </div>
            ) : response && (hardPermConflict || hardRoleConflict) ? (
              <div className="mt-3 flex items-center gap-2" data-testid="rights-agent-apply-blocked">
                <Button data-testid="rights-agent-apply-force" onClick={() => apply(true)} disabled={applying}
                  className="rounded-sm bg-white border border-amber-300 text-amber-700 hover:bg-amber-50 h-8 px-3 text-xs font-semibold">
                  {applying ? "Applying…" : "Apply anyway (override duplicate)"}
                </Button>
                <span className="text-[10px] text-amber-600">Blocked — near-duplicate exists. Click only if truly distinct.</span>
              </div>
            ) : null}
          </div>

          {Array.isArray(response.permission_conflicts) && response.permission_conflicts.length > 0 && (
            <AgentConflictList
              title="Similar permissions that already exist"
              rows={response.permission_conflicts}
              keyFn={(r) => r.key}
              primaryFn={(r) => r.label}
              secondaryFn={(r) => `${r.key} · ${r.category}`}
            />
          )}
          {Array.isArray(response.role_conflicts) && response.role_conflicts.length > 0 && (
            <AgentConflictList
              title="Existing roles that overlap"
              rows={response.role_conflicts}
              keyFn={(r) => r.role}
              primaryFn={(r) => r.label || r.role}
              secondaryFn={(r) => `${r.permission_count} perms · ${r.shared_permissions?.length || 0} shared`}
            />
          )}
          {response.assignment_analysis && (
            <div className="bg-white border border-indigo-200 rounded-sm p-3 text-xs">
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-indigo-600 mb-1">
                <Sparkle size={10} weight="fill" />Assignment analysis
              </div>
              <ul className="space-y-1 font-mono">
                <li><span className="text-red-600 font-bold">Redundant:</span> {response.assignment_analysis.requested?.redundant?.join(", ") || "—"}</li>
                <li><span className="text-emerald-600 font-bold">Net new:</span> {response.assignment_analysis.requested?.net_new?.join(", ") || "—"}</li>
                <li><span className="text-amber-600 font-bold">Unknown (typo?):</span> {response.assignment_analysis.requested?.unknown?.join(", ") || "—"}</li>
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AgentConflictList({ title, rows, keyFn, primaryFn, secondaryFn }) {
  return (
    <div className="bg-white border border-amber-200 rounded-sm p-3">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-2">
        <Warning size={11} weight="fill" />{title}
      </div>
      <ul className="divide-y divide-slate-100">
        {rows.map((r, i) => (
          <li key={keyFn(r) || i} className="flex items-center gap-2 py-2">
            <span className="text-[9px] font-mono text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-sm">
              {Math.round((r.similarity || 0) * 100)}%
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">{primaryFn(r)}</p>
              <p className="text-[11px] text-slate-500 truncate font-mono">{secondaryFn(r)}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}


// ─── Role template editor (create / extend custom roles) ─────────
function RoleTemplateEditor({ roles, catalog, onChanged }) {
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState("");
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [permissions, setPermissions] = useState(new Set());
  const [cloneFrom, setCloneFrom] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [checking, setChecking] = useState(false);

  const chooseClone = (key) => {
    const r = roles.find((x) => x.role === key);
    setCloneFrom(key);
    if (r) setPermissions(new Set(r.permissions || []));
  };

  const check = async () => {
    if (!role.trim() && !label.trim()) return;
    setChecking(true);
    try {
      const r = await advisorCheckRole({ role: role.trim(), label: label.trim(), permissions: [...permissions] });
      setConflicts(r.matches || []);
    } catch { setConflicts([]); }
    finally { setChecking(false); }
  };

  const save = async () => {
    setErr(""); setMsg("");
    if (!role.trim()) { setErr("Role key is required (lowercase letters / digits / underscore)"); return; }
    setSaving(true);
    try {
      await upsertAccessRole({
        role: role.trim().toLowerCase(),
        label: label.trim() || role.trim(),
        description: description.trim(),
        permissions: [...permissions],
      });
      setMsg(`Role '${role}' saved with ${permissions.size} permission(s)`);
      setRole(""); setLabel(""); setDescription(""); setPermissions(new Set()); setCloneFrom(""); setConflicts([]);
      onChanged?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const toggle = (k) => {
    const n = new Set(permissions);
    if (n.has(k)) n.delete(k); else n.add(k);
    setPermissions(n);
  };

  return (
    <div data-testid="role-template-editor" className="border border-slate-200 rounded-sm bg-white">
      <button data-testid="role-template-toggle" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 transition-colors">
        <div className="flex items-center gap-2">
          <Plus size={14} weight="bold" className="text-slate-500" />
          <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-slate-700">Create / extend a role</h3>
          <span className="text-[10px] text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-sm">{roles.length} defined</span>
        </div>
        <span className="text-[10px] text-slate-400">{open ? "close" : "open"}</span>
      </button>
      {open && (
        <div className="border-t border-slate-200 p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Role key</label>
              <Input data-testid="role-template-key" value={role} onChange={(e) => setRole(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
                placeholder="e.g. warehouse_clerk" className="rounded-sm border-slate-300 h-9 text-sm font-mono" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Label</label>
              <Input data-testid="role-template-label" value={label} onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Warehouse Clerk" className="rounded-sm border-slate-300 h-9 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Description</label>
            <Input data-testid="role-template-desc" value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Short description shown in the role list" className="rounded-sm border-slate-300 h-9 text-sm" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Clone permissions from…</label>
            <select data-testid="role-template-clone" value={cloneFrom} onChange={(e) => chooseClone(e.target.value)}
              className="w-full rounded-sm border-slate-300 h-9 text-sm px-2">
              <option value="">— Start blank —</option>
              {roles.map((r) => <option key={r.role} value={r.role}>{r.label || r.role} ({r.permissions.length} perms)</option>)}
            </select>
          </div>

          {/* Permissions matrix */}
          <div className="border border-slate-200 rounded-sm max-h-52 overflow-y-auto" data-testid="role-template-perms">
            {catalog.map((cat) => (
              <div key={cat.category}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 px-2 py-1 bg-slate-50 border-b border-slate-200">{cat.category}</p>
                <ul className="divide-y divide-slate-100">
                  {cat.items.map((p) => (
                    <li key={p.key} className="flex items-center gap-2 px-2 py-1 text-[12px]">
                      <input type="checkbox" data-testid={`role-template-perm-${p.key}`}
                        checked={permissions.has(p.key)} onChange={() => toggle(p.key)}
                        className="rounded border-slate-300" />
                      <span className="text-slate-700 flex-1">{p.label}</span>
                      <span className="text-[9px] text-slate-400 font-mono">{p.key}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 pt-1">
            <Button data-testid="role-template-check" onClick={check} disabled={checking}
              className="rounded-sm bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-50 h-9 px-3 text-xs font-semibold">
              {checking ? "Checking…" : "Check for overlaps"}
            </Button>
            <Button data-testid="role-template-save" onClick={save} disabled={saving}
              className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 px-3 text-sm font-semibold">
              {saving ? "Saving…" : (role && roles.some((r) => r.role === role) ? "Update role" : "Create role")}
            </Button>
            <div className="ml-auto text-[11px] text-slate-500">{permissions.size} permission(s) selected</div>
          </div>

          {conflicts.length > 0 && (
            <AgentConflictList
              title="Existing roles that overlap"
              rows={conflicts}
              keyFn={(r) => r.role}
              primaryFn={(r) => r.label || r.role}
              secondaryFn={(r) => `${r.permission_count} perms · ${r.shared_permissions?.length || 0} shared`}
            />
          )}
          {msg && <div data-testid="role-template-msg" className="text-xs text-green-700 bg-green-50 px-3 py-2 rounded-sm border border-green-200">{msg}</div>}
          {err && <div data-testid="role-template-err" className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-sm border border-red-200">{err}</div>}
        </div>
      )}
    </div>
  );
}




// ─── Agent history feed (inside Rights tab) ──────────────────────
function AgentHistoryPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await advisorHistory({ limit: 10 });
      setRows(r.events || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="agent-history-panel" className="border border-slate-200 rounded-sm bg-white">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200">
        <Robot size={14} weight="bold" className="text-slate-500" />
        <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-slate-700">Recent agent actions</h3>
        <span className="text-[10px] text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-sm">{rows.length}</span>
        <button onClick={load} disabled={loading} data-testid="agent-history-refresh"
          className="ml-auto text-[10px] text-slate-400 hover:text-slate-700 underline">
          {loading ? "…" : "refresh"}
        </button>
      </div>
      {rows.length === 0 ? (
        <p className="px-4 py-3 text-xs text-slate-400 italic">No agent actions yet. Ask the agent above to create a permission or role.</p>
      ) : (
        <ul className="divide-y divide-slate-100 max-h-60 overflow-y-auto">
          {rows.map((ev) => {
            const ago = ev.minutes_ago == null ? "—"
              : ev.minutes_ago < 1 ? "just now"
              : ev.minutes_ago < 60 ? `${ev.minutes_ago}m ago`
              : ev.minutes_ago < 1440 ? `${Math.floor(ev.minutes_ago / 60)}h ago`
              : `${Math.floor(ev.minutes_ago / 1440)}d ago`;
            const intentColor = ev.intent === "create_permission"
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : ev.intent === "create_role"
                ? "bg-blue-50 text-blue-700 border-blue-200"
              : "bg-amber-50 text-amber-700 border-amber-200";
            return (
              <li key={ev.apply_id} data-testid={`agent-history-row-${ev.apply_id}`}
                className={`flex items-center gap-3 px-4 py-2 text-sm ${ev.undone ? "bg-slate-50/60" : ""}`}>
                <span className={`text-[9px] font-bold border px-1.5 py-0.5 rounded-sm ${intentColor}`}>
                  {(ev.intent || "").replace("_", " ")}
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm truncate ${ev.undone ? "line-through text-slate-400" : "text-slate-800"}`}>
                    {ev.summary}
                  </p>
                  <p className="text-[10px] text-slate-500 truncate">
                    {ev.actor} · {ago}{ev.undone ? " · undone" : ""}
                  </p>
                </div>
                {ev.can_undo && !ev.undone && (
                  <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-sm">
                    UNDO OPEN
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


// ─── Activity tab (recent quote changes + agent actions) ─────────
function ActivityPanel({ user }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [window, setWindow] = useState(60 * 24); // 1 day default

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetchRecentQuoteChanges({ limit: 40, since_minutes: window });
      setQuotes(r.events || []);
    } catch { setQuotes([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [window]);

  const WINDOWS = [
    { label: "1 hour",  mins: 60 },
    { label: "1 day",   mins: 60 * 24 },
    { label: "7 days",  mins: 60 * 24 * 7 },
    { label: "30 days", mins: 60 * 24 * 30 },
  ];

  return (
    <div className="space-y-5" data-testid="activity-panel">
      {/* Agent actions (reuses the Rights-tab component for consistency). */}
      <AgentHistoryPanel />

      {/* Recent quote changes */}
      <div className="border border-slate-200 rounded-sm bg-white" data-testid="recent-quote-changes">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200">
          <Receipt size={14} weight="bold" className="text-slate-500" />
          <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-slate-700">Recent quote changes</h3>
          <span className="text-[10px] text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-sm">{quotes.length}</span>
          <select data-testid="recent-quote-window" value={window} onChange={(e) => setWindow(parseInt(e.target.value, 10))}
            className="ml-auto rounded-sm border-slate-300 h-7 text-xs px-2">
            {WINDOWS.map((w) => <option key={w.mins} value={w.mins}>{w.label}</option>)}
          </select>
          <button onClick={load} disabled={loading} data-testid="recent-quote-refresh"
            className="text-[10px] text-slate-400 hover:text-slate-700 underline">
            {loading ? "…" : "refresh"}
          </button>
        </div>
        {quotes.length === 0 ? (
          <p className="px-4 py-3 text-xs text-slate-400 italic">{loading ? "Loading…" : "No quote activity in the selected window."}</p>
        ) : (
          <ul className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
            {quotes.map((ev) => {
              const short = (ev.heading_id || "").startsWith("CART-") ? ev.heading_id.slice(5, 13) : (ev.heading_id || "").slice(0, 8);
              const entityColor = {
                quote: "bg-blue-50 text-blue-700 border-blue-200",
                quote_line: "bg-indigo-50 text-indigo-700 border-indigo-200",
                comment: "bg-amber-50 text-amber-700 border-amber-200",
                email: "bg-emerald-50 text-emerald-700 border-emerald-200",
                quote_pdf: "bg-purple-50 text-purple-700 border-purple-200",
              }[ev.entity] || "bg-slate-100 text-slate-600 border-slate-200";
              const ts = (ev.ts_utc || "").slice(5, 16).replace("T", " ");
              return (
                <li key={ev.id} data-testid={`recent-quote-row-${ev.id}`}
                  className="flex items-center gap-3 px-4 py-2 text-sm hover:bg-slate-50">
                  <span className={`text-[9px] font-bold border px-1.5 py-0.5 rounded-sm ${entityColor}`}>
                    {(ev.entity || "").replace("_", " ")}
                  </span>
                  <a href={`/quote/${encodeURIComponent(ev.heading_id)}`} target="_blank" rel="noreferrer"
                    data-testid={`recent-quote-link-${ev.heading_id}`}
                    className="text-[11px] font-mono text-blue-700 hover:underline whitespace-nowrap">
                    #{short}
                  </a>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-800 truncate">{ev.summary}</p>
                    <p className="text-[10px] text-slate-500 truncate">
                      {ev.quote_company || "—"} · {ev.actor_email || "—"} · {ev.action_type}
                      {ev.quote_status ? ` · ${ev.quote_status}` : ""}
                    </p>
                  </div>
                  <span className="text-[10px] font-mono text-slate-400 whitespace-nowrap">{ts}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}



// ─── Email routing & test-send panel ───
function EmailPanel({ user }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [routes, setRoutes] = useState({});
  const [categories, setCategories] = useState([]);
  const [smtpReady, setSmtpReady] = useState(false);
  const [smtpUser, setSmtpUser] = useState("");
  const [testTo, setTestTo] = useState(user?.email || "");
  const [testBrand, setTestBrand] = useState("US");
  const [testCategory, setTestCategory] = useState("sales_usa");
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const d = await fetchEmailRouting(user.email);
      setRoutes(d.routes || {});
      setCategories(d.categories || []);
      setSmtpReady(!!d.smtp_ready);
      setSmtpUser(d.smtp_user || "");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load routing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setInfo("");
    try {
      const d = await saveEmailRouting({ requester_email: user.email, routes });
      setRoutes(d.routes || {});
      setInfo("Routing saved.");
      setTimeout(() => setInfo(""), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setError("");
    setTestResult(null);
    try {
      const d = await sendTestEmail({
        requester_email: user.email,
        to_email: testTo,
        brand: testBrand,
        category: testCategory,
      });
      setTestResult(d);
    } catch (err) {
      setError(err.response?.data?.detail || "Test send failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-email-panel">
      <div
        className={`rounded-sm border px-3 py-2.5 text-xs leading-snug ${
          smtpReady
            ? "bg-emerald-50 border-emerald-200 text-emerald-900"
            : "bg-amber-50 border-amber-200 text-amber-900"
        }`}
        data-testid="admin-email-smtp-status"
      >
        <div className="font-bold mb-0.5">
          {smtpReady ? "SMTP configured" : "SMTP not configured"}
        </div>
        <div>
          {smtpReady
            ? <>Sending from <b>{smtpUser}</b>. If test sends fail with "Auth failed", the password in <code>.env</code> is not a valid 16-char Gmail App Password.</>
            : <>Set <code>SMTP_USER</code> and <code>SMTP_PASSWORD</code> (16-char Gmail App Password) in backend/.env and restart the backend.</>
          }
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 px-3 py-2 rounded-sm border border-red-200">
          <Warning size={13} weight="bold" /> {error}
        </div>
      )}
      {info && (
        <div className="flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 px-3 py-2 rounded-sm border border-emerald-200">
          <CheckCircle size={13} weight="bold" /> {info}
        </div>
      )}

      <div className="border border-slate-200 rounded-sm p-3 bg-slate-50/50 space-y-2">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-700">Send test email</div>
        <div className="flex flex-col gap-2">
          <Input
            data-testid="admin-email-test-to"
            placeholder="recipient@example.com"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            className="rounded-sm border-slate-300 h-9 text-sm"
          />
          <div className="flex gap-2">
            <select
              data-testid="admin-email-test-brand"
              value={testBrand}
              onChange={(e) => setTestBrand(e.target.value)}
              className="rounded-sm border border-slate-300 h-9 text-sm px-2 bg-white"
            >
              <option value="US">US</option>
              <option value="ACC">ACC (Canada)</option>
            </select>
            <select
              data-testid="admin-email-test-category"
              value={testCategory}
              onChange={(e) => setTestCategory(e.target.value)}
              className="rounded-sm border border-slate-300 h-9 text-sm px-2 bg-white flex-1"
            >
              {categories.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </div>
          <Button
            data-testid="admin-email-test-send"
            disabled={testing || !testTo.trim()}
            onClick={handleTest}
            className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 text-sm font-semibold"
          >
            <PaperPlaneRight size={13} weight="bold" className="mr-1.5" />
            {testing ? "Sending…" : "Send test email"}
          </Button>
        </div>

        {testResult && (
          <div
            data-testid="admin-email-test-result"
            className={`text-xs leading-snug rounded-sm border px-3 py-2 mt-2 ${
              testResult.ok
                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                : "bg-red-50 border-red-200 text-red-900"
            }`}
          >
            <div className="font-bold">
              {testResult.ok ? "Sent" : testResult.mocked ? "Skipped (mocked)" : "Failed"}
            </div>
            <div className="mt-1">
              <b>From:</b> {testResult.from_name} &lt;{testResult.from_email}&gt;<br/>
              <b>Reply-To:</b> {testResult.reply_to}
            </div>
            {testResult.message && (
              <div className="mt-1 break-all"><b>Details:</b> {testResult.message}</div>
            )}
          </div>
        )}
      </div>

      <div className="border border-slate-200 rounded-sm">
        <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-700">
          Default email recipients & routing
        </div>
        <p className="px-3 pt-2 text-[11px] text-slate-500 leading-snug">
          <b>Customer / Dealer / Vendor</b> rows are the <b>Reply-To</b> header used when a guest triggers an email — for logged-in users, Reply-To is always their own address.
          <br />
          <b>Internal</b> rows are the actual recipients of automated workflows (engineering review of a signed commissioning, ops archive cc on every signed PDF, price-list-download notifications).
        </p>
        {loading ? (
          <div className="p-3 text-xs text-slate-400">Loading…</div>
        ) : (
          <div className="p-3 space-y-3">
            {["customer", "dealer", "vendor", "internal"].map((aud) => {
              const rows = categories.filter((c) => (c.audience || "customer") === aud);
              if (!rows.length) return null;
              const groupLabel = {
                customer: "Customer-facing (Reply-To)",
                dealer: "Dealer-facing (Reply-To)",
                vendor: "Vendor-facing (Reply-To)",
                internal: "Internal automated workflows",
              }[aud];
              return (
                <div key={aud} className="space-y-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-100 pb-1">
                    {groupLabel}
                  </div>
                  {rows.map((c) => (
                    <div key={c.key} className="flex flex-col sm:flex-row sm:items-start gap-1.5">
                      <label className="text-xs font-semibold text-slate-700 sm:w-44 shrink-0 sm:pt-1.5">
                        {c.label}
                        {c.description && (
                          <div className="text-[10px] font-normal text-slate-400 leading-tight mt-0.5">
                            {c.description}
                          </div>
                        )}
                      </label>
                      <Input
                        data-testid={`admin-email-route-${c.key}`}
                        type="email"
                        value={routes[c.key] || ""}
                        onChange={(e) => setRoutes({ ...routes, [c.key]: e.target.value })}
                        placeholder="inbox@example.com"
                        className="rounded-sm border-slate-300 h-8 text-xs flex-1"
                      />
                    </div>
                  ))}
                </div>
              );
            })}
            <div className="pt-2">
              <Button
                data-testid="admin-email-save-routing"
                disabled={saving}
                onClick={handleSave}
                className="rounded-sm bg-emerald-700 text-white hover:bg-emerald-800 h-9 text-sm font-semibold"
              >
                <CheckCircle size={13} weight="bold" className="mr-1.5" />
                {saving ? "Saving…" : "Save routing"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// iter136.28 — PWA branding tab. Super-admin only.
//   * Edits ``title`` / ``short_name`` / ``description`` / ``theme_color``.
//   * Single drag-and-drop upload field — server auto-generates the
//     192/512 ``any`` and maskable PNGs from one source image.
//   * Live preview shows tab title, install card, Android adaptive
//     mask (circle clip), and iOS rounded-square so the super admin
//     sees what each surface will render before saving.
//   * Reset-to-factory wipes the override and restores the static
//     bundled defaults.
function PwaBrandingPanel({ user }) {
  const isSuperAdmin = Boolean(user?.is_super_admin);
  const [state, setState] = useState(null);
  const [draft, setDraft] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [resetting, setResetting] = useState(false);

  const reload = () => {
    setError("");
    fetchPwaBranding()
      .then((b) => {
        setState(b);
        setDraft({
          title: b.title || "",
          short_name: b.short_name || "",
          description: b.description || "",
          theme_color: b.theme_color || "#ed1b24",
        });
      })
      .catch((e) => setError(e?.response?.data?.detail || e.message || "Failed to load branding"));
  };
  useEffect(() => { reload(); }, []);

  if (!isSuperAdmin) {
    return (
      <div className="text-sm text-slate-600 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3">
        Only super admins can edit the PWA title and icon.
      </div>
    );
  }
  if (!state) {
    return <div className="text-xs text-slate-500">Loading branding…</div>;
  }

  const dirty =
    draft.title !== state.title ||
    draft.short_name !== state.short_name ||
    draft.description !== state.description ||
    draft.theme_color !== state.theme_color;

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      const next = await updatePwaBranding(draft);
      setState(next);
      refreshBranding(next);
      window.dispatchEvent(new Event("usair:branding-changed"));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError("Image exceeds 5 MB limit");
      return;
    }
    setUploading(true); setError("");
    try {
      const next = await uploadPwaIcon(file);
      setState(next);
      refreshBranding(next);
      window.dispatchEvent(new Event("usair:branding-changed"));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to upload icon");
    } finally {
      setUploading(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset PWA title and icon back to the bundled defaults? This cannot be undone.")) return;
    setResetting(true); setError("");
    try {
      const next = await resetPwaBranding();
      setState(next);
      refreshBranding(next);
      window.dispatchEvent(new Event("usair:branding-changed"));
      setDraft({
        title: next.title || "",
        short_name: next.short_name || "",
        description: next.description || "",
        theme_color: next.theme_color || "#ed1b24",
      });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to reset");
    } finally {
      setResetting(false);
    }
  };

  const iconUrl = state.icons?.["any-512"] || "/icon-512.png";
  const maskableUrl = state.icons?.["maskable-512"] || "/icon-maskable-512.png";

  return (
    <div className="space-y-5" data-testid="pwa-branding-panel">
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-sm border border-red-200">
          <Warning size={14} weight="bold" /> {error}
        </div>
      )}

      <div className="text-xs text-slate-600 bg-blue-50 border border-blue-200 rounded-sm px-3 py-2 space-y-1">
        <div><b>How this works.</b> Changes apply immediately to <i>browser</i> tabs after one refresh. Already-installed home-screen apps cache the icon + title at install time — users with the old PWA installed must remove and reinstall it from the home screen to pick up new branding.</div>
        {state.has_overrides && state.updated_at ? (
          <div className="text-[11px] text-slate-500">Last updated by <span className="font-mono">{state.updated_by}</span> at {new Date(state.updated_at).toLocaleString()}.</div>
        ) : (
          <div className="text-[11px] text-slate-500 italic">Currently using bundled defaults — no override set.</div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">App title (full)</label>
            <Input
              data-testid="pwa-branding-title"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              maxLength={120}
              className="rounded-sm border-slate-300 h-9 text-sm mt-1"
              placeholder="Air Compressor Canada"
            />
            <div className="text-[10px] text-slate-400 mt-0.5">Browser tab + Android install splash · {draft.title.length}/120</div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Home-screen label (short)</label>
            <Input
              data-testid="pwa-branding-short-name"
              value={draft.short_name}
              onChange={(e) => setDraft({ ...draft, short_name: e.target.value })}
              maxLength={60}
              className="rounded-sm border-slate-300 h-9 text-sm mt-1"
              placeholder="ACC"
            />
            <div className="text-[10px] text-slate-400 mt-0.5">Shown under the icon on iOS / Android. Aim for ≤ 12 chars · {draft.short_name.length}/60</div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Description</label>
            <textarea
              data-testid="pwa-branding-description"
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              maxLength={500}
              rows={3}
              className="w-full rounded-sm border border-slate-300 text-sm p-2 mt-1 resize-y"
              placeholder="One-sentence summary shown in the install prompt."
            />
            <div className="text-[10px] text-slate-400 mt-0.5">Shown in install prompts · {draft.description.length}/500</div>
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Theme colour</label>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="color"
                data-testid="pwa-branding-theme-color-picker"
                value={draft.theme_color}
                onChange={(e) => setDraft({ ...draft, theme_color: e.target.value })}
                className="w-10 h-9 rounded-sm border border-slate-300 cursor-pointer"
              />
              <Input
                data-testid="pwa-branding-theme-color-text"
                value={draft.theme_color}
                onChange={(e) => setDraft({ ...draft, theme_color: e.target.value.toLowerCase() })}
                maxLength={7}
                pattern="^#[0-9a-fA-F]{6}$"
                className="rounded-sm border-slate-300 h-9 text-sm font-mono w-28"
              />
              <span className="text-[10px] text-slate-400">Status bar + splash background</span>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Button
              data-testid="pwa-branding-save"
              disabled={!dirty || saving}
              onClick={handleSave}
              className="rounded-sm bg-emerald-700 text-white hover:bg-emerald-800 h-9 px-4 text-sm font-semibold"
            >
              <CheckCircle size={13} weight="bold" className="mr-1.5" />
              {saving ? "Saving…" : "Save"}
            </Button>
            {dirty && (
              <button
                data-testid="pwa-branding-discard"
                onClick={() => setDraft({
                  title: state.title || "",
                  short_name: state.short_name || "",
                  description: state.description || "",
                  theme_color: state.theme_color || "#ed1b24",
                })}
                className="text-xs text-slate-500 hover:text-slate-700 underline"
              >
                Discard
              </button>
            )}
            <span className="text-[10px] text-slate-400 ml-auto">Icon uploads save instantly — only the text fields above need this button.</span>
          </div>
        </div>

        <div className="space-y-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Preview</div>

          <div className="border border-slate-200 rounded-sm bg-slate-100 p-2">
            <div className="text-[10px] text-slate-500 mb-1">Browser tab</div>
            <div className="bg-white rounded-t-md px-2 py-1.5 inline-flex items-center gap-2 max-w-full border border-slate-200" style={{ minWidth: 200 }}>
              <img src={iconUrl} alt="" className="w-4 h-4" />
              <span className="text-xs text-slate-700 truncate">{draft.title || state.title}</span>
            </div>
          </div>

          <div className="border border-slate-200 rounded-sm bg-slate-50 p-3">
            <div className="text-[10px] text-slate-500 mb-2">Install prompt (Android)</div>
            <div className="bg-white rounded-md p-3 flex items-center gap-3 border border-slate-200">
              <img src={iconUrl} alt="" className="w-12 h-12 rounded-md flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-800 truncate">{draft.title || state.title}</div>
                <div className="text-[11px] text-slate-500 line-clamp-2">{draft.description || state.description}</div>
              </div>
              <button className="text-[11px] font-bold text-blue-700 px-2 py-1 rounded-sm border border-blue-200 flex-shrink-0">Install</button>
            </div>
          </div>

          <div className="border border-slate-200 rounded-sm bg-slate-50 p-3">
            <div className="text-[10px] text-slate-500 mb-2">Home-screen tile</div>
            <div className="grid grid-cols-2 gap-3 items-start">
              <div className="text-center">
                <div
                  className="mx-auto rounded-full overflow-hidden bg-white shadow"
                  style={{ width: 72, height: 72 }}
                  title="Android adaptive mask (circle)"
                >
                  <img src={maskableUrl} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="text-[10px] text-slate-700 mt-1.5 truncate font-medium">{draft.short_name || state.short_name}</div>
                <div className="text-[9px] text-slate-400">Android (circle mask)</div>
              </div>
              <div className="text-center">
                <div
                  className="mx-auto overflow-hidden bg-white shadow"
                  style={{ width: 72, height: 72, borderRadius: 16 }}
                  title="iOS rounded square"
                >
                  <img src={iconUrl} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="text-[10px] text-slate-700 mt-1.5 truncate font-medium">{draft.short_name || state.short_name}</div>
                <div className="text-[9px] text-slate-400">iOS (rounded square)</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border border-slate-200 rounded-sm p-4 bg-slate-50 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <div className="text-sm font-bold text-slate-800">Replace icon</div>
              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-200">Saves instantly</span>
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Upload one image (PNG, JPG, or WEBP, ≤ 5 MB). Server auto-pads non-square uploads, samples the background colour from the corners, and generates the four PWA sizes (192 / 512, regular + maskable safe-zone for Android adaptive masks). Applies the moment the upload finishes — no separate save needed.
            </div>
          </div>
        </div>
        <label className="block">
          <input
            type="file"
            data-testid="pwa-branding-icon-upload"
            accept="image/png,image/jpeg,image/webp"
            disabled={uploading}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) handleUpload(f);
            }}
            className="hidden"
          />
          <span className={`inline-flex items-center gap-2 px-3 h-9 rounded-sm text-sm font-semibold cursor-pointer ${uploading ? "bg-slate-300 text-slate-500" : "bg-blue-700 text-white hover:bg-blue-800"}`}>
            <Upload size={14} weight="bold" />
            {uploading ? "Uploading…" : "Choose image…"}
          </span>
        </label>
      </div>

      <div className="flex items-center justify-end pt-2 border-t border-slate-100">
        <button
          data-testid="pwa-branding-reset"
          disabled={!state.has_overrides || resetting}
          onClick={handleReset}
          className="text-xs text-red-600 hover:text-red-800 underline flex items-center gap-1.5 disabled:text-slate-300 disabled:no-underline disabled:cursor-not-allowed"
        >
          <ArrowsClockwise size={12} weight="bold" />
          {resetting ? "Resetting…" : "Reset to bundled defaults"}
        </button>
      </div>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────
// iter137.4 — Knowledge Base URLs panel (super-admin only).
// Lets the super-admin define the list of source URLs (web pages and
// PDFs) the warranty voice-analysis AI will consult per brand.
// Storage only in this iteration; the AI consumer is Phase 2.
// ─────────────────────────────────────────────────────────────────

function KnowledgeBaseUrlsPanel({ user }) {
  const [items, setItems] = useState({ US: { urls: [] }, ACC: { urls: [] } });
  const [validKinds, setValidKinds] = useState(["web", "pdf", "auto"]);
  const [validBrands, setValidBrands] = useState(["US", "ACC"]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const isSuper = !!(user?.is_super_admin);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/kb-urls")
      .then((r) => {
        setItems(r.data.items || { US: { urls: [] }, ACC: { urls: [] } });
        setValidKinds(r.data.valid_kinds || ["web", "pdf", "auto"]);
        setValidBrands(r.data.valid_brands || ["US", "ACC"]);
      })
      .catch(() => toast.error("Could not load Knowledge Base URLs"))
      .finally(() => setLoading(false));
  }, []);

  const setBrandUrls = (brand, urls) => {
    setItems((it) => ({ ...it, [brand]: { ...(it[brand] || {}), urls } }));
  };

  const save = async (brand) => {
    setSaving((s) => ({ ...s, [brand]: true }));
    try {
      const urls = (items[brand]?.urls || []).filter((u) => (u.url || "").trim());
      const { data } = await api.put("/admin/kb-urls", { brand, urls });
      setItems((it) => ({ ...it, [brand]: { ...(it[brand] || {}), urls: data.urls } }));
      toast.success(`Saved ${data.count} URL${data.count === 1 ? "" : "s"} for ${brand}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Save failed for ${brand}`);
    } finally {
      setSaving((s) => ({ ...s, [brand]: false }));
    }
  };

  if (loading) return <div className="text-xs text-slate-400 p-3">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="admin-kb-urls-panel">
      <p className="text-xs text-slate-500 leading-snug">
        These URLs feed the warranty-claim voice-analysis AI. When a customer records a problem description,
        the AI will reference these sources to suggest possible causes &amp; troubleshooting steps.
        Mix web pages and PDFs — set <b>kind</b> to <i>auto</i> to let the system decide based on the file extension.
        {!isSuper && <span className="block mt-1 text-amber-700 font-semibold">View-only — only super-admins can save changes.</span>}
      </p>
      {validBrands.map((brand) => (
        <BrandUrlList
          key={brand}
          brand={brand}
          urls={items[brand]?.urls || []}
          onChange={(u) => setBrandUrls(brand, u)}
          onSave={() => save(brand)}
          saving={!!saving[brand]}
          updatedAt={items[brand]?.updated_at}
          updatedBy={items[brand]?.updated_by}
          validKinds={validKinds}
          readOnly={!isSuper}
        />
      ))}
    </div>
  );
}

function BrandUrlList({ brand, urls, onChange, onSave, saving, updatedAt, updatedBy, validKinds, readOnly }) {
  const update = (idx, patch) => {
    const next = urls.map((u, i) => (i === idx ? { ...u, ...patch } : u));
    onChange(next);
  };
  const remove = (idx) => onChange(urls.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= urls.length) return;
    const next = urls.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  const add = () => onChange([...urls, { url: "", label: "", kind: "auto" }]);

  return (
    <div className="border border-slate-200 rounded-sm">
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-700">
          {brand === "US" ? "US Air Compressor" : "Air Compressor Canada"} ({brand}) · {urls.length} URL{urls.length === 1 ? "" : "s"}
        </div>
        {updatedAt && (
          <div className="text-[10px] text-slate-400">
            Last saved {new Date(updatedAt).toLocaleString()} by {updatedBy || "—"}
          </div>
        )}
      </div>
      <div className="p-3 space-y-2">
        {urls.length === 0 && (
          <div className="text-xs text-slate-400 italic py-2">No URLs configured yet.</div>
        )}
        {urls.map((u, i) => (
          <div
            key={i}
            data-testid={`admin-kb-url-row-${brand}-${i}`}
            className="grid grid-cols-12 gap-1.5 items-center"
          >
            <Input
              data-testid={`admin-kb-url-input-${brand}-${i}`}
              type="url"
              placeholder="https://example.com/manual.pdf"
              value={u.url || ""}
              onChange={(e) => update(i, { url: e.target.value })}
              disabled={readOnly}
              className="col-span-6 rounded-sm border-slate-300 h-8 text-xs"
            />
            <Input
              data-testid={`admin-kb-url-label-${brand}-${i}`}
              type="text"
              placeholder="Label (optional)"
              value={u.label || ""}
              onChange={(e) => update(i, { label: e.target.value })}
              disabled={readOnly}
              className="col-span-3 rounded-sm border-slate-300 h-8 text-xs"
            />
            <select
              data-testid={`admin-kb-url-kind-${brand}-${i}`}
              value={u.kind || "auto"}
              onChange={(e) => update(i, { kind: e.target.value })}
              disabled={readOnly}
              className="col-span-1 rounded-sm border border-slate-300 h-8 text-[11px] bg-white"
            >
              {validKinds.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <div className="col-span-2 flex items-center gap-1 justify-end">
              <button
                type="button"
                onClick={() => move(i, -1)}
                disabled={readOnly || i === 0}
                className="p-1 rounded-sm hover:bg-slate-100 disabled:opacity-30"
                title="Move up"
              ><ArrowUp size={12} weight="bold" /></button>
              <button
                type="button"
                onClick={() => move(i, 1)}
                disabled={readOnly || i === urls.length - 1}
                className="p-1 rounded-sm hover:bg-slate-100 disabled:opacity-30"
                title="Move down"
              ><ArrowDown size={12} weight="bold" /></button>
              <button
                type="button"
                data-testid={`admin-kb-url-delete-${brand}-${i}`}
                onClick={() => remove(i)}
                disabled={readOnly}
                className="p-1 rounded-sm hover:bg-rose-50 text-rose-600 disabled:opacity-30"
                title="Remove"
              ><Trash size={12} weight="bold" /></button>
            </div>
          </div>
        ))}
        {!readOnly && (
          <div className="flex items-center justify-between pt-2">
            <Button
              data-testid={`admin-kb-add-${brand}`}
              onClick={add}
              variant="outline"
              className="rounded-sm h-8 text-xs"
            >
              <Plus size={12} weight="bold" className="mr-1" /> Add URL
            </Button>
            <Button
              data-testid={`admin-kb-save-${brand}`}
              onClick={onSave}
              disabled={saving}
              className="rounded-sm bg-emerald-700 text-white hover:bg-emerald-800 h-8 text-xs"
            >
              <CheckCircle size={12} weight="bold" className="mr-1" />
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}


// ─── iter137.5 — Supporting-evidence library admin panel ───────────
// Super-admins curate keyword-tagged reference photos / videos / text /
// PDFs / external links. The /api/warranty/claims/{id}/evidence-suggest
// endpoint scores claims' findings_text against these keywords and
// surfaces matches during the supporting-evidence step.

function EvidenceLibraryPanel({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const isSuper = !!(user?.is_super_admin);

  useEffect(() => {
    setLoading(true);
    api.get("/warranty/admin/evidence-library")
      .then((r) => setItems(r.data.items || []))
      .catch(() => toast.error("Could not load Evidence library"))
      .finally(() => setLoading(false));
  }, []);

  const update = (idx, patch) => {
    setItems(items.map((it, i) => i === idx ? { ...it, ...patch } : it));
  };
  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));
  const add = () => setItems([...items, { title: "", description: "", keywords: [], url: "", media_type: "text" }]);

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = items
        .filter((it) => (it.title || "").trim())
        .map((it) => ({
          title: it.title,
          description: it.description || "",
          keywords: typeof it.keywords === "string"
            ? it.keywords.split(",").map((k) => k.trim()).filter(Boolean)
            : (it.keywords || []),
          url: it.url || null,
          media_type: it.media_type || "text",
        }));
      const r = await api.put("/warranty/admin/evidence-library", { items: cleaned });
      toast.success(`Saved ${r.data.count} entr${r.data.count === 1 ? "y" : "ies"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-xs text-slate-400 p-3">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="admin-evidence-library-panel">
      <p className="text-xs text-slate-500 leading-snug">
        Curate reference evidence (photos, videos, text, links) tagged with keywords.
        When a customer fills in their findings text on a warranty claim, the system
        scores the text against these keywords and surfaces matching entries on the
        <i> supporting-evidence </i> step.
        {!isSuper && <span className="block mt-1 text-amber-700 font-semibold">View-only — only super-admins can save changes.</span>}
      </p>
      <div className="space-y-2">
        {items.length === 0 && (
          <div className="text-xs text-slate-400 italic py-2">No entries yet.</div>
        )}
        {items.map((it, i) => (
          <div key={i} data-testid={`admin-evidence-row-${i}`}
            className="border border-slate-200 rounded-sm p-3 space-y-2 bg-slate-50">
            <div className="grid grid-cols-12 gap-1.5">
              <Input
                placeholder="Title"
                value={it.title || ""}
                onChange={(e) => update(i, { title: e.target.value })}
                disabled={!isSuper}
                data-testid={`admin-evidence-title-${i}`}
                className="col-span-7 rounded-sm border-slate-300 h-8 text-xs"
              />
              <select
                value={it.media_type || "text"}
                onChange={(e) => update(i, { media_type: e.target.value })}
                disabled={!isSuper}
                data-testid={`admin-evidence-type-${i}`}
                className="col-span-3 rounded-sm border border-slate-300 h-8 text-[11px] bg-white"
              >
                <option value="text">text</option>
                <option value="image">image</option>
                <option value="video">video</option>
                <option value="pdf">pdf</option>
                <option value="link">link</option>
              </select>
              <button
                type="button"
                onClick={() => remove(i)}
                disabled={!isSuper}
                data-testid={`admin-evidence-delete-${i}`}
                className="col-span-2 rounded-sm hover:bg-rose-50 text-rose-600 disabled:opacity-30 inline-flex items-center justify-center"
                title="Remove"
              ><Trash size={12} weight="bold" /></button>
            </div>
            <Input
              placeholder="Optional URL (https://…)"
              value={it.url || ""}
              onChange={(e) => update(i, { url: e.target.value })}
              disabled={!isSuper}
              data-testid={`admin-evidence-url-${i}`}
              className="rounded-sm border-slate-300 h-8 text-xs"
            />
            <Input
              placeholder="Keywords (comma-separated, e.g. overheat, airend, oil)"
              value={Array.isArray(it.keywords) ? (it.keywords || []).join(", ") : (it.keywords || "")}
              onChange={(e) => update(i, { keywords: e.target.value })}
              disabled={!isSuper}
              data-testid={`admin-evidence-keywords-${i}`}
              className="rounded-sm border-slate-300 h-8 text-xs"
            />
            <textarea
              placeholder="Description (what the customer should look for)"
              value={it.description || ""}
              onChange={(e) => update(i, { description: e.target.value })}
              disabled={!isSuper}
              data-testid={`admin-evidence-desc-${i}`}
              rows={2}
              className="w-full rounded-sm border border-slate-300 text-xs p-2 disabled:bg-slate-100"
            />
          </div>
        ))}
      </div>
      {isSuper && (
        <div className="flex items-center justify-between pt-2">
          <Button
            data-testid="admin-evidence-add"
            onClick={add}
            variant="outline"
            className="rounded-sm h-8 text-xs"
          >
            <Plus size={12} weight="bold" className="mr-1" /> Add entry
          </Button>
          <Button
            data-testid="admin-evidence-save"
            onClick={save}
            disabled={saving}
            className="rounded-sm bg-emerald-700 text-white hover:bg-emerald-800 h-8 text-xs"
          >
            <CheckCircle size={12} weight="bold" className="mr-1" />
            {saving ? "Saving…" : "Save library"}
          </Button>
        </div>
      )}
    </div>
  );
}

